import { useCallback, useRef } from 'react'
import { useIntl } from 'intl'
import logger from 'logger'
import { dataLayer } from 'analytics'
import cookieStorage from 'cookie-storage'
import localStorage from 'local-storage'
import { constants } from 'helpers'
import { useSubscription } from 'modules/subscription'
import { useUser } from 'modules/user'
import { cnstrcTrackPurchaseData } from 'modules/constructorIO'


const cookiesToDelete: string[] = [
  constants.cookieNames.discountCoupon,
  constants.cookieNames.appliedCouponCode,
  constants.cookieNames.isFreeTrialCoupon,
  constants.cookieNames.country,
  constants.cookieNames.utmMediumBeforeSubscription,
  constants.cookieNames.utmSourceBeforeSubscription,
  constants.cookieNames.flowName,
  constants.cookieNames.synergyParams,
  constants.cookieNames.tophatterParams,
  constants.cookieNames.rootCountdown,
  constants.cookieNames.freePerfumeCountdown,
  constants.cookieNames.giftSubscriptionOfferCountdown,
  constants.cookieNames.selectedPlan,
  constants.cookieNames.giftSubscriptionOffer,
]

const localStorageToDelete: string[] = [
  constants.localStorageNames.shippingFormValues,
  constants.localStorageNames.authGender,
  constants.localStorageNames.authEmail,
  constants.localStorageNames.lastAddedToCartProductMetadata,
]

type UseProcessAnalyticsParams = {
  purchaseResult: PaymentModule.PurchaseSession
}

// put all after-purchase events her
export const useProcessAnalytics = () => {
  const intl = useIntl()
  const { subscription } = useSubscription()
  const { user } = useUser()
  const hasNeverSubscribedRef = useRef(subscription.hasNeverSubscribed)

  const email = user?.email

  return useCallback((params: UseProcessAnalyticsParams) => {
    const { purchaseResult } = params

    const {
      hasEcommerce,
      hasSubscription,
      total,
      subscription,
      coupon,
      attentiveOrders,
    } = modifyPurchaseResult(purchaseResult, email, intl)

    const isResubscribe = !hasNeverSubscribedRef.current && hasSubscription

    if (isResubscribe) {
      dataLayer.track('resubscribe', {
        resubscribePath: 'from payment page',
      })
    }

    const tophatterParams = cookieStorage.getItem(constants.cookieNames.tophatterParams) || {}
    const synergyParams = cookieStorage.getItem(constants.cookieNames.synergyParams) || {}

    if (subscription) {
      dataLayer.track('subscription thank you page', {
        subscription: {
          chargeAmount: total,
          coupon: coupon?.code || null,
          couponType: coupon?.type || null,
          ...subscription,
        },
        tophatterParams,
        synergyParams,
      })
    }

    if (hasEcommerce) {
      dataLayer.track('ecommerce thank you page', {
        total,
      })

      // ConstructorIO purchase data tracking (It makes no sense for us to track purchases that did not contain e-commerce items)
      cnstrcTrackPurchaseData(purchaseResult)
    }

    attentiveOrders.forEach((order) => {
      dataLayer.track('attentive purchase', order)
      // attentive.track('purchase', order)
    })

    localStorageToDelete.forEach((name) => localStorage.removeItem(name))

    setTimeout(() => {
      cookiesToDelete.forEach((name) => cookieStorage.removeItem(name))
    }, 10000)
  }, [ email, intl ])
}

const modifyPurchaseResult = (purchaseResult: PaymentModule.PurchaseSession, email: string, intl: Intl.Manager) => {
  const { total, orders, discounts } = purchaseResult.data

  const attentiveOrders: Analytics.DataLayerEvents['attentive purchase'][] = []

  let hasEcommerce = false
  let hasSubscription = false

  let subscription: {
    id: number
    plan: string
    isGift: boolean
  } = null

  orders.forEach(({ number: orderId, items }) => {
    const attentiveOrder: Analytics.DataLayerEvents['attentive purchase'] = {
      orderId,
      email,
      items: [],
      order: {
        orderId,
      },
      user: {
        email,
      },
    }

    items.forEach((item) => {
      if (item.__typename === 'SubscriptionPurchaseItem') {
        subscription = {
          id: item.id,
          plan: item.plan.name,
          isGift: false,
        }

        hasSubscription = true

        attentiveOrder.items.push({
          productId: `SCENTBIRD_SUBSCRIPTION_${item.id}`,
          productVariantId: item.plan.name,
          price: {
            value: item.total / 100,
            currency: intl.currency,
          },
          quantity: 1,
        })
      }
      else if (item.__typename === 'GiftSubscriptionPurchaseItem') {
        if (!subscription) {
          subscription = {
            id: item.giftSubscriptionId,
            plan: 'MONTHLY',
            isGift: true,
          }
        }

        attentiveOrder.items.push({
          productId: `GIFT_SUBSCRIPTION_${item.giftSubscriptionId}`,
          productVariantId: String(item.month),
          price: {
            value: item.total / 100,
            currency: intl.currency,
          },
          quantity: 1,
        })
      }
      else if (item.__typename === 'ProductPurchaseItem') {
        if (!item.product) {
          logger.error(`ProductPurchaseItem is missing 'product' property`, item)

          return
        }

        hasEcommerce = true

        attentiveOrder.items.push({
          productId: String(item.product.id),
          productVariantId: `${item.product.volume} ${item.product.unit}`,
          name: item.product.fullName || `${item.product.brandInfo.name} ${item.product.name}`,
          price: {
            value: item.total / 100,
            currency: intl.currency,
          },
          quantity: item.quantity,
        })
      }
    })

    attentiveOrders.push(attentiveOrder)
  })

  let coupon: {
    code: string
    type: string
  }

  discounts.some((item) => {
    if (item.coupon) {
      coupon = item.coupon
      return true
    }

    return false
  })

  return {
    total: total / 100,
    hasEcommerce,
    hasSubscription,
    subscription,
    coupon,
    attentiveOrders,
  }
}
